import { useCallback, useEffect, useMemo, useState } from "react";
import { Divider } from "antd";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import BaseButton from "@/components_v2/Button/BaseButton";
import BaseCheckbox from "@/components_v2/FormFields/Checkbox/BaseCheckbox";
import { AnswerResponse } from "@/redux/survey/types";
import { SelectionToolbarProps } from "@/features/survey-views/form/types";
import {
  useLazyGetAnswersQuery,
  useLazyGetSurveyByIdQuery,
  useLazyGetSurveyStatisticsQuery,
} from "@/redux/service";
import { SharingRights, SurveyType } from "@/shared/utils/constants";
import { SurveyViewPublicationDestination } from "@/redux/survey-views/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";

import "../styles.css";

const SelectionToolbar = ({ form }: SelectionToolbarProps) => {
  const { t } = useTranslation();

  const [getAnswers] = useLazyGetAnswersQuery();
  const currentUser = useSelector(selectCurrentUser);
  const [selectionTypeAnswers, setSelectionTypeAnswers] = useState<
    Record<string, AnswerResponse[]>
  >({});
  const [getSurveyStatistics, surveyStatisticsResponse] =
    useLazyGetSurveyStatisticsQuery();
  const [getSurveyById, surveyResponse] = useLazyGetSurveyByIdQuery();

  const surveyId = useWatch({
    control: form.control,
    name: "survey",
  });
  const surveyViewPublicationDestination = useWatch({
    control: form.control,
    name: "config.publicationDestination",
  });
  const currentSelectedAnswers = useWatch({
    control: form.control,
    name: "answers",
  });

  const filters = useMemo(
    () => ({
      survey: surveyId,
      organization: currentUser?.organization?.id,
      pagination: "off" as const,
      mode: "base" as const,
      hasBlocks: undefined,
    }),
    [surveyId, currentUser?.organization?.id],
  );

  const surveyStatistics = useMemo(() => {
    if (!surveyStatisticsResponse.isSuccess) return null;

    const data = surveyStatisticsResponse.data;
    return {
      totalNonPromptAnsweredQuestions: data.reduce(
        (sum, section) =>
          sum +
          (section.answeredRequiredNonPromptQuestionsCount || 0) +
          (section.answeredOptionalNonPromptQuestionsCount || 0),
        0,
      ),
      totalAnsweredBlocks: data.reduce(
        (sum, section) => sum + (section.answeredBlocksCount || 0),
        0,
      ),
      totalPublicBlocks: data.reduce(
        (sum, section) =>
          sum + (section.attachedBlocksPerSharingRight?.PUBLIC || 0),
        0,
      ),
      totalPrmeBlocks: data.reduce(
        (sum, section) =>
          sum + (section.attachedBlocksPerSharingRight?.PRME_COMMUNITY || 0),
        0,
      ),
      totalPrivateBlocks: data.reduce(
        (sum, section) =>
          sum + (section.attachedBlocksPerSharingRight?.PRIVATE || 0),
        0,
      ),
    };
  }, [surveyStatisticsResponse]);

  useEffect(() => {
    if (currentUser && surveyId) {
      getSurveyStatistics({ survey: surveyId });
      getSurveyById({
        surveyId,
        filters: { organization: currentUser.organization?.id },
      });
    }
  }, [surveyId, currentUser]);

  const updateFormAnswersForBlocks = useCallback(
    (
      answersWithBlocks: AnswerResponse[],
      sharingRightFilter: string,
      checked: boolean,
    ) => {
      const currentAnswerMap = new Map(
        currentSelectedAnswers.map((answer) => [answer.answer, answer]),
      );

      answersWithBlocks.forEach((answer) => {
        const filteredBlocks = answer.attachedBlocks
          .filter((block) => block.sharingRights === sharingRightFilter)
          .map((block) => ({
            id: block.id,
            sharingRights: block.sharingRights,
          }));

        if (filteredBlocks.length === 0) return;

        const existingAnswer = currentAnswerMap.get(answer.id);

        if (checked) {
          if (existingAnswer) {
            const existingBlockIds = new Set(
              existingAnswer.attachedBlocks.map((b) => b.id),
            );
            const newBlocks = filteredBlocks.filter(
              (block) => !existingBlockIds.has(block.id),
            );

            if (newBlocks.length > 0) {
              currentAnswerMap.set(answer.id, {
                ...existingAnswer,
                attachedBlocks: [
                  ...existingAnswer.attachedBlocks,
                  ...newBlocks,
                ],
              });
            }
          } else {
            currentAnswerMap.set(answer.id, {
              answer: answer.id,
              question: answer.question,
              attachedBlocks: filteredBlocks,
            });
          }
        } else {
          if (existingAnswer) {
            const blocksToRemove = new Set(filteredBlocks.map((b) => b.id));
            const updatedBlocks = existingAnswer.attachedBlocks.filter(
              (block) => {
                const shouldRemove = blocksToRemove.has(block.id);

                if (
                  shouldRemove &&
                  block.sharingRights === SharingRights.PUBLIC.value &&
                  surveyViewPublicationDestination ===
                    SurveyViewPublicationDestination.WEBSITE
                ) {
                  return true;
                }

                return !shouldRemove;
              },
            );

            if (updatedBlocks.length > 0) {
              currentAnswerMap.set(answer.id, {
                ...existingAnswer,
                attachedBlocks: updatedBlocks,
              });
            } else {
              currentAnswerMap.delete(answer.id);
            }
          }
        }
      });

      form.setValue("answers", Array.from(currentAnswerMap.values()));
    },
    [surveyViewPublicationDestination, currentSelectedAnswers, form],
  );

  const getIsNonEvidenceDisabled = useMemo(
    () => surveyStatistics?.totalNonPromptAnsweredQuestions === 0,
    [surveyStatistics],
  );

  const getIsEvidenceDisabled = useMemo(
    () => surveyStatistics?.totalAnsweredBlocks === 0,
    [surveyStatistics],
  );

  const getIsPublicEvidenceDisabled = useMemo(
    () =>
      surveyViewPublicationDestination ===
        SurveyViewPublicationDestination.WEBSITE ||
      surveyStatistics?.totalPublicBlocks === 0,
    [surveyViewPublicationDestination, surveyStatistics],
  );

  const getIsPrmeCommunityEvidenceDisabled = useMemo(
    () => surveyStatistics?.totalPrmeBlocks === 0,
    [surveyStatistics],
  );

  const getIsPrivateEvidenceDisabled = useMemo(
    () => surveyStatistics?.totalPrivateBlocks === 0,
    [surveyStatistics],
  );

  const getSelectionStateFromStats = useCallback(
    (type: "nonEvidence" | "evidence" | "public" | "prme" | "private") => {
      if (!surveyStatistics) return false;

      const selectedEvidenceAnswers = currentSelectedAnswers.filter(
        (answer) => answer.attachedBlocks.length > 0,
      );

      const selectedBlockCounts = selectedEvidenceAnswers.reduce(
        (acc, answer) => {
          answer.attachedBlocks.forEach((block) => {
            if (block.sharingRights === SharingRights.PUBLIC.value)
              acc.public++;
            if (block.sharingRights === SharingRights.PRME_COMMUNITY.value)
              acc.prme++;
            if (block.sharingRights === SharingRights.PRIVATE.value)
              acc.private++;
          });
          return acc;
        },
        { public: 0, prme: 0, private: 0 },
      );
      switch (type) {
        case "nonEvidence": {
          const selectedNonEvidenceCount = currentSelectedAnswers.filter(
            (answer) => answer.attachedBlocks.length === 0,
          ).length;
          return (
            selectedNonEvidenceCount >=
            surveyStatistics.totalNonPromptAnsweredQuestions
          );
        }

        case "evidence": {
          // Evidence is selected when all available evidence blocks are selected
          const totalAvailableBlocks =
            surveyStatistics.totalPublicBlocks +
            surveyStatistics.totalPrmeBlocks +
            surveyStatistics.totalPrivateBlocks;
          const totalSelectedBlocks =
            selectedBlockCounts.public +
            selectedBlockCounts.prme +
            selectedBlockCounts.private;
          return (
            totalAvailableBlocks > 0 &&
            totalSelectedBlocks >= totalAvailableBlocks
          );
        }

        case "public":
          return (
            surveyStatistics.totalPublicBlocks > 0 &&
            selectedBlockCounts.public >= surveyStatistics.totalPublicBlocks
          );

        case "prme":
          return (
            surveyStatistics.totalPrmeBlocks > 0 &&
            selectedBlockCounts.prme >= surveyStatistics.totalPrmeBlocks
          );

        case "private":
          return (
            surveyStatistics.totalPrivateBlocks > 0 &&
            selectedBlockCounts.private >= surveyStatistics.totalPrivateBlocks
          );

        default:
          return false;
      }
    },
    [surveyStatistics, currentSelectedAnswers],
  );

  const getIsNonEvidenceSelected = useMemo(() => {
    if (getIsNonEvidenceDisabled) return false;
    return getSelectionStateFromStats("nonEvidence");
  }, [getSelectionStateFromStats, getIsNonEvidenceDisabled]);

  const getIsEvidenceSelected = useMemo(() => {
    if (getIsEvidenceDisabled) return false;
    return getSelectionStateFromStats("evidence");
  }, [getSelectionStateFromStats, getIsEvidenceDisabled]);

  const getIsPublicEvidenceSelected = useMemo(() => {
    if (
      surveyViewPublicationDestination ===
      SurveyViewPublicationDestination.WEBSITE
    )
      return true;
    if (getIsPublicEvidenceDisabled) return false;
    return getSelectionStateFromStats("public");
  }, [
    surveyViewPublicationDestination,
    getSelectionStateFromStats,
    getIsPublicEvidenceDisabled,
  ]);

  const getIsPrmeCommunityEvidenceSelected = useMemo(() => {
    if (getIsPrmeCommunityEvidenceDisabled) return false;
    return getSelectionStateFromStats("prme");
  }, [getSelectionStateFromStats, getIsPrmeCommunityEvidenceDisabled]);

  const getIsPrivateEvidenceSelected = useMemo(() => {
    if (getIsPrivateEvidenceDisabled) return false;
    return getSelectionStateFromStats("private");
  }, [getSelectionStateFromStats, getIsPrivateEvidenceDisabled]);

  const updateFormAnswers = useCallback(
    (answersData: AnswerResponse[], checked: boolean) => {
      if (checked) {
        const currentAnswerMap = new Map(
          currentSelectedAnswers.map((answer) => [answer.answer, answer]),
        );

        answersData.forEach((answer) => {
          const newBlocks = answer.attachedBlocks.map((block) => ({
            id: block.id,
            sharingRights: block.sharingRights,
          }));

          const existingAnswer = currentAnswerMap.get(answer.id);

          if (existingAnswer) {
            const existingBlockIds = new Set(
              existingAnswer.attachedBlocks.map((b) => b.id),
            );
            const blocksToAdd = newBlocks.filter(
              (block) => !existingBlockIds.has(block.id),
            );

            if (blocksToAdd.length > 0) {
              currentAnswerMap.set(answer.id, {
                ...existingAnswer,
                attachedBlocks: [
                  ...existingAnswer.attachedBlocks,
                  ...blocksToAdd,
                ],
              });
            }
          } else {
            currentAnswerMap.set(answer.id, {
              answer: answer.id,
              question: answer.question,
              attachedBlocks: newBlocks,
            });
          }
        });

        form.setValue("answers", Array.from(currentAnswerMap.values()));
      } else {
        const answerIdsToRemove = new Set(
          answersData.map((answer) => answer.id),
        );

        const updatedAnswers = currentSelectedAnswers.reduce(
          (acc, answer) => {
            if (!answerIdsToRemove.has(answer.answer)) {
              acc.push(answer);
              return acc;
            }

            // For website surveys, keep public blocks when removing other blocks
            if (
              surveyViewPublicationDestination ===
              SurveyViewPublicationDestination.WEBSITE
            ) {
              const publicBlocks = answer.attachedBlocks.filter(
                (block) => block.sharingRights === SharingRights.PUBLIC.value,
              );

              if (publicBlocks.length > 0) {
                acc.push({
                  ...answer,
                  attachedBlocks: publicBlocks,
                });
              }
            }

            return acc;
          },
          [] as typeof currentSelectedAnswers,
        );
        form.setValue("answers", updatedAnswers);
      }
    },
    [currentSelectedAnswers, form],
  );

  const fetchAnswersByType = useCallback(
    async (hasBlocks?: boolean, sharingRightFilter?: string) => {
      try {
        if (!filters.organization) return [];
        const params = { ...filters };
        if (hasBlocks !== undefined) {
          // @ts-expect-error has blocks exists on filters
          params.hasBlocks = hasBlocks;
        }

        const response = await getAnswers(params);
        const answers = response.data as AnswerResponse[];

        if (sharingRightFilter) {
          return answers.filter((answer) =>
            answer.attachedBlocks.some(
              (block) => block.sharingRights === sharingRightFilter,
            ),
          );
        }

        return answers;
      } catch (error) {
        console.error("Failed to fetch answers:", error);
        return [];
      }
    },
    [getAnswers, filters],
  );

  const handleSelectAnswers = useCallback(
    async (
      type: keyof typeof selectionTypeAnswers,
      checked: boolean,
      fetchParams?: { hasBlocks?: boolean; sharingRight?: string },
    ) => {
      let answersData = selectionTypeAnswers[type];

      if (!answersData) {
        answersData = await fetchAnswersByType(
          fetchParams?.hasBlocks,
          fetchParams?.sharingRight,
        );

        setSelectionTypeAnswers((prev) => ({
          ...prev,
          [type]: answersData || [],
        }));
      }

      if (fetchParams?.sharingRight) {
        updateFormAnswersForBlocks(
          answersData,
          fetchParams.sharingRight,
          checked,
        );
      } else {
        updateFormAnswers(answersData, checked);
      }
    },
    [
      selectionTypeAnswers,
      fetchAnswersByType,
      updateFormAnswersForBlocks,
      updateFormAnswers,
    ],
  );

  const handleSelectNonEvidenceAnswers = useCallback(
    (checked: boolean) =>
      handleSelectAnswers("nonEvidenceAnswers", checked, { hasBlocks: false }),
    [handleSelectAnswers],
  );

  const handleSelectEvidenceAnswers = useCallback(
    (checked: boolean) =>
      handleSelectAnswers("evidenceAnswers", checked, { hasBlocks: true }),
    [handleSelectAnswers],
  );

  const handleSelectPublicEvidenceAnswers = useCallback(
    (checked: boolean) =>
      handleSelectAnswers("publicEvidenceAnswers", checked, {
        hasBlocks: true,
        sharingRight: SharingRights.PUBLIC.value,
      }),
    [handleSelectAnswers],
  );

  const handleSelectPrmeCommunityEvidenceAnswers = useCallback(
    (checked: boolean) =>
      handleSelectAnswers("prmeCommunityEvidenceAnswers", checked, {
        hasBlocks: true,
        sharingRight: SharingRights.PRME_COMMUNITY.value,
      }),
    [handleSelectAnswers],
  );

  const handleSelectPrivateEvidenceAnswers = useCallback(
    (checked: boolean) =>
      handleSelectAnswers("privateEvidenceAnswers", checked, {
        hasBlocks: true,
        sharingRight: SharingRights.PRIVATE.value,
      }),
    [handleSelectAnswers],
  );

  const handleSelectAll = useCallback(async () => {
    try {
      const answersData = await fetchAnswersByType();

      // Process all answer types in parallel
      const nonEvidenceAnswers = answersData.filter(
        (answer) => answer.attachedBlocks.length === 0,
      );
      const evidenceAnswers = answersData.filter(
        (answer) => answer.attachedBlocks.length > 0,
      );

      // Use a single filter pass for sharing rights
      const sharingRightGroups = evidenceAnswers.reduce<
        Record<string, AnswerResponse[]>
      >(
        (acc, answer) => {
          const hasPublic = answer.attachedBlocks.some(
            (block) => block.sharingRights === SharingRights.PUBLIC.value,
          );
          const hasPrme = answer.attachedBlocks.some(
            (block) =>
              block.sharingRights === SharingRights.PRME_COMMUNITY.value,
          );
          const hasPrivate = answer.attachedBlocks.some(
            (block) => block.sharingRights === SharingRights.PRIVATE.value,
          );

          if (hasPublic) acc.public?.push(answer);
          if (hasPrme) acc.prme?.push(answer);
          if (hasPrivate) acc.private?.push(answer);

          return acc;
        },
        { public: [], prme: [], private: [] },
      );

      setSelectionTypeAnswers({
        nonEvidenceAnswers,
        evidenceAnswers,
        publicEvidenceAnswers: sharingRightGroups.public || [],
        prmeCommunityEvidenceAnswers: sharingRightGroups.prme || [],
        privateEvidenceAnswers: sharingRightGroups.private || [],
      });

      updateFormAnswers(answersData, true);
    } catch (error) {
      console.error("Failed to select all answers:", error);
    }
  }, [fetchAnswersByType, updateFormAnswers]);

  if (surveyStatisticsResponse.isError) {
    return <Navigate to="/" />;
  }

  return (
    <div className="survey-view-selection-toolbar flex flex-col gap-300 full-width items-start">
      <h4 className="header-s-medium">
        {t("surveyViews.labels.formContentLabel")}
      </h4>
      {currentUser && (
        <>
          <BaseButton onClick={handleSelectAll}>
            {t("surveyViews.labels.selectAll")}
          </BaseButton>

          <Divider style={{ margin: "12px 0" }} />

          <div className="flex flex-col gap-300">
            <h5 className="body-m-medium">
              {t("surveyViews.labels.selectionByAnswerType")}
            </h5>

            <div className="flex flex-col gap-250">
              {surveyResponse.data?.type === SurveyType.SIP.value && (
                <BaseCheckbox
                  label={t("surveyViews.labels.nonEvidenceAnswers")}
                  checked={getIsNonEvidenceSelected}
                  onChange={handleSelectNonEvidenceAnswers}
                  disabled={getIsNonEvidenceDisabled}
                />
              )}
              <BaseCheckbox
                label={t("surveyViews.labels.evidenceAnswers")}
                checked={getIsEvidenceSelected}
                onChange={handleSelectEvidenceAnswers}
                disabled={getIsEvidenceDisabled}
              />
              <BaseCheckbox
                className="survey-view-selection-toolbar-sub-checkboxes"
                label={t("surveyViews.labels.publicEvidence")}
                checked={getIsPublicEvidenceSelected}
                onChange={handleSelectPublicEvidenceAnswers}
                disabled={getIsPublicEvidenceDisabled}
              />
              <BaseCheckbox
                className="survey-view-selection-toolbar-sub-checkboxes"
                label={t("surveyViews.labels.prmeCommunityEvidence")}
                checked={getIsPrmeCommunityEvidenceSelected}
                onChange={handleSelectPrmeCommunityEvidenceAnswers}
                disabled={getIsPrmeCommunityEvidenceDisabled}
              />
              <BaseCheckbox
                className="survey-view-selection-toolbar-sub-checkboxes"
                label={t("surveyViews.labels.privateEvidence")}
                checked={getIsPrivateEvidenceSelected}
                onChange={handleSelectPrivateEvidenceAnswers}
                disabled={getIsPrivateEvidenceDisabled}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SelectionToolbar;
